import type {
  WorkoutRecord,
  WorkoutSupersetRecord,
  WorkoutExerciseRecord,
  WorkoutExerciseDimensionRecord,
  WorkoutSetRecord,
  WorkoutSetValueRecord,
} from '~/powersync/generated/schema';

/**
 * The full local record graph of a single workout: the root plus every
 * denormalized child table (each child carries its own `workoutId` for
 * PowerSync sync-rule bucketing).
 */
export interface WorkoutGraph {
  workout: WorkoutRecord;
  supersets: WorkoutSupersetRecord[];
  exercises: WorkoutExerciseRecord[];
  dimensions: WorkoutExerciseDimensionRecord[];
  sets: WorkoutSetRecord[];
  setValues: WorkoutSetValueRecord[];
}

/**
 * Map lookup tolerant of a null foreign key — PowerSync columns are all
 * nullable, and a null FK (or a key with no remap) means the parent was
 * filtered out, so the child is skipped.
 */
const lookup = (
  map: Map<string, string>,
  key: string | null,
): string | undefined => (key == null ? undefined : map.get(key));

export interface CloneWorkoutOptions {
  /** Mints a fresh uuid; injected so the deep copy stays pure + testable. */
  generateId: () => string;
  /** ISO timestamp stamped onto every cloned record's created/updated fields. */
  now: string;
  /**
   * Human-facing id for the new workout. `null` for a keep-both duplicate (the
   * server backfills it via PowerSync); the original's niceId for the
   * replace-in-place start-flow clone.
   */
  niceId: string | null;
  /**
   * Back-reference to the source workout. `null` for a repeatable duplicate
   * (the `clonedFromId` column is `@unique`, so a second duplicate of the same
   * source would violate the constraint); the source id for the start flow.
   */
  clonedFromId: string | null;
  /** Planned date for the new workout (the date the user picks when duplicating). */
  plannedAt: string | null;
  /**
   * Session start timestamp for the new workout. `null`/omitted for a keep-both
   * duplicate (a fresh planned session). The start flow passes `now` so the clone
   * is born STARTED in the same transaction — otherwise a start races a transient
   * PLANNED clone at the inherited niceId and `/progress` redirects to the builder,
   * so the finish summary never renders.
   */
  startedAt?: string | null;
}

/**
 * Pure deep-copy of a workout's record graph. Shared by both the keep-both
 * `duplicateWorkout` mutation and the replace-in-place `cloneWorkout` (start
 * flow) — the only difference between them is the {@link CloneWorkoutOptions}
 * (niceId / clonedFromId / plannedAt) and, for the start flow, the caller
 * soft-deleting the original.
 *
 * Every id is regenerated and every denormalized `workoutId` is rewritten to the
 * new workout. Session state is reset (`difficulty`/`startedAt`/`finishedAt` on
 * the workout, `completedAt` on sets). Structure and planned values (supersets,
 * exercises + their notes/dimensions, sets and their set values) are preserved.
 *
 * Orphaned children are skipped: a soft-deleted exercise leaves its
 * sets/values/dimensions dangling (those tables have no `deletedAt`), so a set
 * whose exercise is absent — or a value whose set/dimension was dropped — is
 * omitted rather than aborting the whole clone.
 */
export class WorkoutCloner {
  static clone(source: WorkoutGraph, opts: CloneWorkoutOptions): WorkoutGraph {
    const { generateId, now } = opts;
    const newWorkoutId = generateId();

    const workout: WorkoutRecord = {
      ...source.workout,
      id: newWorkoutId,
      niceId: opts.niceId,
      clonedFromId: opts.clonedFromId,
      plannedAt: opts.plannedAt,
      difficulty: null,
      startedAt: opts.startedAt ?? null,
      finishedAt: null,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
    };

    const supersetIdMap = new Map<string, string>();
    const supersets = source.supersets.map((superset) => {
      const id = generateId();
      supersetIdMap.set(superset.id, id);
      return {
        ...superset,
        id,
        workoutId: newWorkoutId,
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
      };
    });

    const exerciseIdMap = new Map<string, string>();
    const exercises: WorkoutExerciseRecord[] = [];
    for (const exercise of source.exercises) {
      const newSupersetId = lookup(supersetIdMap, exercise.supersetId);
      if (!newSupersetId) continue;
      const id = generateId();
      exerciseIdMap.set(exercise.id, id);
      exercises.push({
        ...exercise,
        id,
        supersetId: newSupersetId,
        workoutId: newWorkoutId,
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
      });
    }

    const dimensionIdMap = new Map<string, string>();
    const dimensions: WorkoutExerciseDimensionRecord[] = [];
    for (const dimension of source.dimensions) {
      const newExerciseId = lookup(exerciseIdMap, dimension.exerciseId);
      if (!newExerciseId) continue;
      const id = generateId();
      dimensionIdMap.set(dimension.id, id);
      dimensions.push({
        ...dimension,
        id,
        exerciseId: newExerciseId,
        workoutId: newWorkoutId,
        createdAt: now,
        updatedAt: now,
      });
    }

    const setIdMap = new Map<string, string>();
    const sets: WorkoutSetRecord[] = [];
    for (const set of source.sets) {
      const newExerciseId = lookup(exerciseIdMap, set.exerciseId);
      if (!newExerciseId) continue;
      const id = generateId();
      setIdMap.set(set.id, id);
      sets.push({
        ...set,
        id,
        exerciseId: newExerciseId,
        workoutId: newWorkoutId,
        completedAt: null,
        createdAt: now,
        updatedAt: now,
      });
    }

    const setValues: WorkoutSetValueRecord[] = [];
    for (const setValue of source.setValues) {
      const newSetId = lookup(setIdMap, setValue.setId);
      const newDimensionId = lookup(
        dimensionIdMap,
        setValue.exerciseDimensionId,
      );
      if (!newSetId || !newDimensionId) continue;
      setValues.push({
        ...setValue,
        id: generateId(),
        setId: newSetId,
        exerciseDimensionId: newDimensionId,
        workoutId: newWorkoutId,
        createdAt: now,
        updatedAt: now,
      });
    }

    return { workout, supersets, exercises, dimensions, sets, setValues };
  }
}
